// Lớp phủ: hộp thoại, ngăn kéo bên phải, menu thả xuống. Đóng bằng Esc / bấm ra ngoài; giữ tiêu điểm hợp lý.
import { useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type ReactNode, type RefObject } from 'react';
import { cx } from '../lib/cx';
import { Btn } from './button';
import { Icon, type IconName } from './icon';

// Ngăn xếp Esc (LIFO): mỗi lớp nổi đang mở (modal, drawer, menu, Tìm nhanh) đăng ký khi gắn; một lần Esc chỉ đóng lớp
// trên cùng (modal chồng trên drawer, hộp xác nhận trên drawer trang). Lớp không đóng được bằng Esc (Modal không có
// onClose) vẫn chiếm chỗ ⇒ Esc không xuyên xuống lớp bên dưới.
const escLayers: { fire: () => void }[] = [];
function onEscapeKey(e: KeyboardEvent) {
  if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
  escLayers[escLayers.length - 1]?.fire();
}

/** Đăng ký một lớp nổi vào ngăn xếp Esc khi `active`; thứ tự theo lúc gắn (không đổi khi handler đổi tham chiếu). */
export function useEscapeLayer(onEscape?: () => void, active = true) {
  const ref = useRef(onEscape);
  ref.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const layer = { fire: () => ref.current?.() };
    escLayers.push(layer);
    if (escLayers.length === 1) window.addEventListener('keydown', onEscapeKey);
    return () => {
      const i = escLayers.lastIndexOf(layer);
      if (i >= 0) escLayers.splice(i, 1);
      if (!escLayers.length) window.removeEventListener('keydown', onEscapeKey);
    };
  }, [active]);
}
const useEscape = useEscapeLayer;
/** Có lớp nổi nào đang mở (modal, drawer, menu, Tìm nhanh, thanh bên trượt) — phím tắt toàn cục không chạy khi đó. */
export const hasOpenLayer = () => escLayers.length > 0;

// ---------- Tiêu điểm của hộp thoại (Modal, Drawer) ----------
const FOCUSABLE = 'a[href], area[href], button, input:not([type="hidden"]), select, textarea, iframe, summary, [contenteditable=""], [contenteditable="true"], [tabindex]';
/** Phần tử Tab tới được trong `root` theo thứ tự DOM: bỏ phần tử bị khoá, tabindex âm, nằm trong [inert] hoặc không hiển thị. */
export function tabbables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) =>
      el.tabIndex >= 0 &&
      !el.matches(':disabled') &&
      !el.closest('[inert], [hidden]') &&
      // checkVisibility: display:none (VD cột .hide-sm trên điện thoại), visibility:hidden — trình duyệt bỏ qua khi Tab
      (typeof el.checkVisibility !== 'function' || el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true } as CheckVisibilityOptions)),
  );
}

// Ngăn xếp bẫy Tab (LIFO theo lúc gắn, như ngăn xếp Esc): chỉ hộp thoại trên cùng giữ Tab / Shift+Tab ở trong nó.
const traps: { root: () => HTMLElement | null }[] = [];
const isLost = (el: Element | null) => !el || el === document.body || !el.isConnected;
function onTabKey(e: KeyboardEvent) {
  if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey) return;
  const root = traps[traps.length - 1]?.root();
  if (!root?.isConnected) return;
  const active = document.activeElement;
  // Tiêu điểm ở một lớp nổi khác không đăng ký bẫy ⇒ để yên; rơi ra trang phía sau / <body> ⇒ kéo về
  if (!isLost(active) && !root.contains(active) && active!.closest('[role="dialog"], [role="alertdialog"]')) return;
  const list = tabbables(root);
  if (!list.length) {
    e.preventDefault();
    return;
  }
  const first = list[0]!;
  const last = list[list.length - 1]!;
  if (isLost(active) || !root.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
    return;
  }
  // Còn phần tử Tab tới được phía trước / phía sau phần tử đang có tiêu điểm (kể cả khi nó là tiêu đề tabindex=-1)?
  const a = active!;
  const has = (dir: number) => list.some((el) => el !== a && !!(a.compareDocumentPosition(el) & dir));
  if (e.shiftKey ? !has(Node.DOCUMENT_POSITION_PRECEDING) : !has(Node.DOCUMENT_POSITION_FOLLOWING)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }
}

/** Opener của hộp thoại vừa đóng trong cùng lượt commit — hộp thay thế nó (VD hộp "Đang tải…" ⇒ hộp nội dung) nhận lại. */
let handoff: HTMLElement | null = null;
/** Mục của Menu (bị gỡ khi menu đóng) ⇒ nút mở menu (phần tử đầu của .menu-wrap). */
const menuTrigger = (el: HTMLElement | null) => {
  const wrap = el?.closest('.menu')?.parentElement;
  return wrap?.classList.contains('menu-wrap') ? (wrap.firstElementChild as HTMLElement | null) : null;
};

const pushTrap = (layer: { root: () => HTMLElement | null }) => {
  traps.push(layer);
  if (traps.length === 1) document.addEventListener('keydown', onTabKey, true);
};
const popTrap = (layer: { root: () => HTMLElement | null }) => {
  const i = traps.lastIndexOf(layer);
  if (i >= 0) traps.splice(i, 1);
  if (!traps.length) document.removeEventListener('keydown', onTabKey, true);
};

/**
 * Tiêu điểm của một hộp thoại (aria-modal): khi gắn — giữ phần tử bên trong đã tự nhận tiêu điểm (autoFocus), không thì
 * `initialFocus` (bộ chọn CSS trong hộp), không thì tiêu đề (tabindex=-1); bẫy Tab / Shift+Tab ở trong hộp trên cùng; khi gỡ —
 * trả tiêu điểm về phần tử đã mở hộp (nếu còn trên trang và tiêu điểm đang rơi về <body> / còn trong hộp). Dùng cho Modal,
 * Drawer và Tìm nhanh (ô nhập autoFocus, không có tiêu đề).
 */
export function useDialogFocus(root: RefObject<HTMLElement | null>, title?: RefObject<HTMLElement | null>, initialFocus?: string) {
  // Ghi nhận lúc render đầu — trước khi autoFocus bên trong lấy mất tiêu điểm
  const [captured] = useState(() => (typeof document === 'undefined' || isLost(document.activeElement) ? null : (document.activeElement as HTMLElement)));
  const [viaMenu] = useState(() => menuTrigger(captured));
  const initial = useRef(initialFocus);
  useEffect(() => {
    const el = root.current;
    // Opener đã bị gỡ: nằm trong hộp vừa được thay bằng hộp này ⇒ nhận opener của hộp đó; là mục của Menu vừa đóng ⇒ nút mở
    // menu; còn lại ⇒ phần tử đang có tiêu điểm lúc gắn (ngoài hộp)
    const now = document.activeElement as HTMLElement | null;
    const live = (x: HTMLElement | null | undefined) => (x?.isConnected && !el?.contains(x) ? x : null);
    const opener = live(captured) ?? live(handoff) ?? live(viaMenu) ?? (!isLost(now) ? live(now) : null);
    const layer = { root: () => root.current };
    pushTrap(layer);
    if (el && !el.contains(document.activeElement)) {
      const pick = initial.current ? el.querySelector<HTMLElement>(initial.current) : null;
      pick?.focus();
      if (!el.contains(document.activeElement)) title?.current?.focus({ preventScroll: true });
    }
    return () => {
      popTrap(layer);
      // Gỡ thật ⇒ DOM của hộp đã rời trang (cleanup của effect chạy sau pha gỡ DOM). Hộp còn trên trang ⇒ StrictMode chạy lại
      // effect (dev) — không trả tiêu điểm, không làm mất tiêu điểm autoFocus bên trong
      if (el?.isConnected) return;
      const cur = document.activeElement;
      if (opener?.isConnected && (isLost(cur) || !!el?.contains(cur))) opener.focus({ preventScroll: true });
      handoff = opener;
      queueMicrotask(() => {
        if (handoff === opener) handoff = null;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Lớp luôn gắn trên trang nhưng chỉ "mở" theo cờ (thanh bên trượt trên điện thoại): khi `active` — ghi nhận phần tử đang có
 * tiêu điểm (nút mở), đưa tiêu điểm vào phần tử đầu khớp `initialFocus`, bẫy Tab / Shift+Tab ở trong `root`; khi tắt — trả
 * tiêu điểm về nút mở nếu tiêu điểm còn ở trong lớp hoặc đã rơi về <body>.
 */
export function useFocusTrap(root: RefObject<HTMLElement | null>, active: boolean, initialFocus?: string) {
  const initial = useRef(initialFocus);
  useEffect(() => {
    if (!active) return;
    const el = root.current;
    const now = document.activeElement as HTMLElement | null;
    const opener = !isLost(now) && !el?.contains(now) ? now : null;
    const layer = { root: () => root.current };
    pushTrap(layer);
    if (el && !el.contains(document.activeElement)) {
      const pick = initial.current ? el.querySelector<HTMLElement>(initial.current) : null;
      (pick ?? tabbables(el)[0])?.focus({ preventScroll: true });
    }
    return () => {
      popTrap(layer);
      const cur = document.activeElement;
      if (opener?.isConnected && (isLost(cur) || !!el?.contains(cur))) opener.focus({ preventScroll: true });
    };
  }, [active, root]);
}

export type ModalSize = '' | 'sm' | 'lg' | 'xl';
interface DialogProps {
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  foot?: ReactNode;
  /**
   * Bộ chọn CSS (trong hộp) của phần tử nhận tiêu điểm khi mở, nếu bên trong chưa có phần tử tự nhận (autoFocus). Vắng hoặc
   * không tìm thấy / đang khoá ⇒ tiêu đề.
   */
  initialFocus?: string;
}

/** Hộp thoại giữa màn hình: aria-labelledby = tiêu đề; tiêu điểm vào trong khi mở, bẫy Tab, trả về nơi mở khi đóng; Esc ⇒ onClose. */
export function Modal({ title, sub, onClose, children, foot, size = '', initialFocus }: DialogProps & { onClose?: () => void; size?: ModalSize }) {
  useEscape(onClose);
  const ref = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  useDialogFocus(ref, titleRef, initialFocus);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div ref={ref} className={cx('modal', size)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-head">
          <div>
            <h2 id={titleId} ref={titleRef} tabIndex={-1}>
              {title}
            </h2>
            {sub && <div className="small muted">{sub}</div>}
          </div>
          {onClose && <Btn v="ghost" icon="x" onClick={onClose} aria-label="Đóng" />}
        </div>
        <div className="modal-body">{children}</div>
        {foot && <div className="modal-foot">{foot}</div>}
      </div>
    </div>
  );
}

/** Ngăn kéo bên phải: cùng hành vi tiêu điểm / Esc như Modal. */
export function Drawer({ title, sub, onClose, children, foot, wide, head, initialFocus }: DialogProps & { onClose: () => void; wide?: boolean; head?: ReactNode }) {
  useEscape(onClose);
  const ref = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  useDialogFocus(ref, titleRef, initialFocus);
  return (
    <div className="drawer-wrap" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside ref={ref} className={cx('drawer', wide && 'wide')} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="drawer-head">
          <div className="grow">
            {head}
            <h2 id={titleId} ref={titleRef} tabIndex={-1}>
              {title}
            </h2>
            {sub && (
              <div className="small muted" style={{ marginTop: 2 }}>
                {sub}
              </div>
            )}
          </div>
          <Btn v="ghost" icon="x" onClick={onClose} aria-label="Đóng" />
        </div>
        <div className="drawer-body">{children}</div>
        {foot && <div className="drawer-foot">{foot}</div>}
      </aside>
    </div>
  );
}

/** Menu thả xuống; phần tử có data-close đóng menu khi bấm. */
export function Menu({ trigger, children, align = 'left', width = 240 }: { trigger: (toggle: () => void, open: boolean) => ReactNode; children: ReactNode; align?: 'left' | 'right'; width?: number }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  // Giữ menu trong khung nhìn (điện thoại: nút nằm giữa/trái nhưng menu canh phải ⇒ dịch ngang cho vừa)
  useLayoutEffect(() => {
    if (!open || !menuRef.current) return setShift(0);
    const r = menuRef.current.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const base = r.left - shift;
    const right = base + r.width;
    setShift(base < 8 ? 8 - base : right > vw - 8 ? vw - 8 - right : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Đóng bằng chọn mục (data-close) hoặc Esc ⇒ trả tiêu điểm về nút mở menu (mục vừa bấm đã bị gỡ); bấm ra ngoài ⇒ để yên
  const restore = useRef(false);
  const closeToTrigger = () => {
    restore.current = true;
    setOpen(false);
  };
  useEffect(() => {
    if (open || !restore.current) return;
    restore.current = false;
    if (isLost(document.activeElement)) (ref.current?.firstElementChild as HTMLElement | null)?.focus({ preventScroll: true });
  }, [open]);
  // Menu mở trong drawer/modal: Esc chỉ đóng menu
  useEscapeLayer(closeToTrigger, open);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);
  // Tiêu điểm (Tab / Shift+Tab) rời khỏi nút + menu ⇒ đóng, không kéo tiêu điểm về (relatedTarget null — bấm vào vùng không
  // nhận tiêu điểm, cửa sổ mất tiêu điểm — để bấm ra ngoài / Esc xử lý)
  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (open && next && !ref.current?.contains(next)) setOpen(false);
  };
  return (
    <div className="menu-wrap" ref={ref} onBlur={onBlur}>
      {trigger(() => setOpen((o) => !o), open)}
      {open && (
        <div ref={menuRef} className={cx('menu', align)} style={{ width: `min(${width}px, calc(100vw - 32px))`, ...(shift ? { transform: `translateX(${shift}px)` } : {}) }} onClick={(e) => (e.target as HTMLElement).closest('[data-close]') && closeToTrigger()}>
          {children}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ icon, children, onClick, danger, hint, disabled }: { icon?: IconName; children: ReactNode; onClick?: () => void; danger?: boolean; hint?: ReactNode; disabled?: boolean }) {
  return (
    <button type="button" className={cx('menu-item', danger && 'danger')} data-close onClick={onClick} disabled={disabled}>
      {icon && <Icon n={icon} s={15} />}
      <span className="grow">{children}</span>
      {hint && <span className="menu-hint">{hint}</span>}
    </button>
  );
}

/** Nút "Quy tắc" mở danh sách luật nghiệp vụ — thay cho đoạn giải thích dài dưới tiêu đề. */
export function RulesButton({ children, label = 'Quy tắc' }: { children: ReactNode; label?: string }) {
  return (
    <Menu
      align="right"
      width={380}
      trigger={(toggle, open) => (
        <Btn icon="info" v="ghost" aria-expanded={open} onClick={toggle}>
          {label}
        </Btn>
      )}
    >
      <div className="rules">{children}</div>
    </Menu>
  );
}
export function RulesList({ items }: { items: (ReactNode | false | null | undefined)[] }) {
  return (
    <ul className="rules-list">
      {items.filter(Boolean).map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  );
}
